import { AlertColor } from '@mui/material'
import axios, { AxiosError } from 'axios'
import dayjs, { Dayjs } from 'dayjs'
import { ProblemDetails } from './app/api'
import { isProblemDetails } from './app/utils'

export type DateStyle = 'full' | 'long' | 'medium' | 'short' | undefined
export const formatDate = (date: any, dateStyle: DateStyle = 'medium') => {
  if (!date) {
    return
  }

  return dayjs(date).toDate().toLocaleString([], { dateStyle: dateStyle })
}

export const formatDayAndNumber = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date).format('ddd DD')
}

export const formatDayAndNumberWithAbbreviatedMonth = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date).format('ddd DD MMM')
}

export const formatMonthAndYear = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date).format('MMMM YYYY')
}

export const formatTime = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date).format('HH:mm')
}

export const formatDateShort = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date).format('DD/MM/YY')
}

export const formatDateRange = (fromDate: any, toDate: any) => {
  if (!fromDate || !toDate) {
    return
  }

  const from = dayjs(fromDate).toDate()
  const to = dayjs(toDate).toDate()

  const timeOptions: any = { timeStyle: 'short', hour12: false }

  if (from.toLocaleDateString() === to.toLocaleDateString()) {
    return `${from.toLocaleDateString([], { dateStyle: 'short' })} ${from.toLocaleTimeString([], timeOptions)} - ${to.toLocaleTimeString([], timeOptions)}`
  } else {
    return `${from.toLocaleDateString([], { dateStyle: 'short' })} ${from.toLocaleTimeString([], timeOptions)} - ${to.toLocaleDateString([], { dateStyle: 'short' })} ${to.toLocaleTimeString([], timeOptions)}`
  }
}

export const formatDateTime = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date)
    .toDate()
    .toLocaleString([], { dateStyle: 'short', timeStyle: 'short', hour12: false })
}

export const getDateTimeUtcString = (
  date?: string | Dayjs | null,
  time?: string | Dayjs | null,
): string | null => {
  const dateObj = dayjs(date)

  if (!dateObj.isValid()) {
    return null
  }

  const hour = time ? dayjs(time).hour() : 0
  const minute = time ? dayjs(time).minute() : 0

  return dateObj.set('hour', hour).set('minute', minute).toISOString()
}

export const parseDate = (date: any) => {
  if (!date) {
    return
  }

  return dayjs(date)
}

export const capitalize = (str?: string | null): string | null | undefined => {
  if (!str) return str
  const lowerCaseStr = str.toLowerCase()
  return lowerCaseStr.charAt(0).toUpperCase() + lowerCaseStr.slice(1)
}

export interface IDeleteRecordActions {
  openConfirmDialog: (open: boolean, content?: string) => Promise<boolean>
  deleteRecord: (id: number) => Promise<void>
  showAlert: (severity?: AlertColor, message?: string) => void
  deletedCallback?: () => Promise<void>
}

export const openFileInNewWindow = async (document: Blob) => {
  const url = URL.createObjectURL(document)
  window.open(url, '_blank')
}

export const useDeleteRecord = (actions: IDeleteRecordActions, recordName?: string) => {
  return async (id: number) => {
    const confirmed = await actions.openConfirmDialog(
      true,
      `Do you want to delete ${recordName ?? 'record'}?`,
    )
    if (confirmed) {
      try {
        await actions.deleteRecord(id)
        actions.showAlert('success', 'Vessel deleted successfully')

        if (actions.deletedCallback) {
          await actions.deletedCallback()
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError
          if (axiosError.response && isProblemDetails(axiosError.response.data)) {
            actions.showAlert(
              'error',
              (axiosError.response.data as ProblemDetails).detail ?? 'Failed to delete',
            )
          } else {
            actions.showAlert('error', 'Failed to delete')
          }
        }
      }
    }
  }
}

export function removeEmptyOptionalFields<T extends object>(obj: T): T {
  const newObj = { ...obj }

  ;(Object.keys(newObj) as Array<keyof T>).forEach(key => {
    const propertyValue = newObj[key]

    if (propertyValue === undefined) {
      delete newObj[key]
      return
    }

    if (
      typeof propertyValue === 'object' &&
      propertyValue !== null &&
      'value' in propertyValue &&
      (propertyValue as { value: any }).value === undefined
    ) {
      delete newObj[key]
    }
  })

  return newObj
}

export const scrollToFirstMuiError = () => {
  const errorElement = document.querySelector('.MuiFormHelperText-root.Mui-error')

  if (errorElement) {
    const inputContainer = errorElement.closest('.MuiFormControl-root') || errorElement
    inputContainer.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }
}
